import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLang } from "@/i18n";

interface NewsletterSignupProps {
  /** "default" → cream theme · "footer" → dark theme inside footer */
  variant?: "default" | "footer";
}

const NewsletterSignup = ({ variant = "default" }: NewsletterSignupProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { lang, market, t } = useLang();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error(t("newsletter.invalidEmpty"));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t("newsletter.invalidFormat"));
      return;
    }

    if (email.length > 255) {
      toast.error(t("newsletter.invalidLong"));
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("newsletter-subscribe", {
        body: { email: email.trim(), language: lang, market },
      });

      if (error) throw error;

      if (data?.success) {
        if (data.code === "already_subscribed" || data.message === "Šis el. paštas jau užprenumeruotas") {
          toast.info(data.message);
        } else {
          toast.success(data.message, {
            description: t("newsletter.spamNote"),
            duration: 8000,
          });
          setEmail("");
        }
      } else {
        throw new Error(data?.error || "Unknown error");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Įvyko klaida";
      if (errorMessage.includes("Per daug") || errorMessage.includes("429")) {
        toast.error(t("newsletter.tooMany"));
      } else {
        toast.error(t("newsletter.genericError"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isFooter = variant === "footer";

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-md">
      <div className="flex flex-col gap-3">
        <Input
          type="email"
          placeholder={t("newsletter.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={cn(
            "h-12 rounded-xl",
            isFooter
              ? "bg-transparent border-white/20 text-[hsl(var(--footer-fg))] placeholder:text-[hsl(var(--footer-fg))]/40 focus-visible:ring-terracotta focus-visible:border-terracotta"
              : "bg-cream-soft border-ink/10 text-ink placeholder:text-ink/40"
          )}
          disabled={isLoading}
          maxLength={255}
        />
        <Button
          type="submit"
          disabled={isLoading}
          className="h-12 rounded-full px-7 bg-terracotta text-cream hover:bg-terracotta/90 font-semibold w-full sm:w-auto sm:self-start"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              {t("newsletter.submit")}
              <ArrowRight className="ml-1 w-4 h-4" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default NewsletterSignup;
